import {
  Control,
  FieldErrors,
  UseFormWatch,
  UseFormSetValue,
  Controller,
  UseFormTrigger,
  UseFormClearErrors,
} from 'react-hook-form';
import { documentTypeOptions } from '../../../../shared/google-document-types';
import UsersAutocomplete from '../../../../shared/users-autocomplete';
import { useRef, useState } from 'react';
import AvatarChip from '../../../../shared/avatar-chip';
import {
  domainSharingRadioOptions,
  userSharingRadioOptions,
} from '../sharing-options';
import { RulesEngineFormValues } from '../rules-engine-form-page-old';
import {
  sizeComparisonOptions,
  sizeTypeOptions,
} from '../../../../shared/file-size-utils';

import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import Avatar from '@mui/material/Avatar';

interface DriveFiltersStepProps {
  control: Control<RulesEngineFormValues>;
  errors: FieldErrors<RulesEngineFormValues>;
  watch: UseFormWatch<RulesEngineFormValues>;
  setValue: UseFormSetValue<RulesEngineFormValues>;
  trigger: UseFormTrigger<RulesEngineFormValues>;
  clearErrors: UseFormClearErrors<RulesEngineFormValues>;
}

export default function DriveFiltersStep({
  control,
  errors,
  watch,
  setValue,
  trigger,
  clearErrors,
}: DriveFiltersStepProps) {
  const [autoCompleteResponse, setAutoCompleteResponse] = useState('');
  const autocompleteKey = useRef(new Date().getTime().toString());

  const hasFileTypeFilter = watch('hasFileTypeFilter');
  const hasFileSizeFilter = watch('hasFileSizeFilter');
  const sizeComparison = watch('sizeComparison');
  const hasSharingPermissionsFilter = watch('hasSharingPermissionsFilter');
  const selectedSharingOption = watch('selectedSharingOption');
  const hasSelectedDomainSharing = watch('hasSelectedDomainSharing');
  const canFindInSearchResults = watch('triggerInfo.canFindInSearchResults');
  const fileSharedToSpecificUser = watch(
    'triggerInfo.fileSharedToSpecificUser'
  );

  return (
    <Stack gap={2} width={'100%'}>
      <Typography variant="caption">
        You can optionally provide a filter on the drive file type, size, and
        sharing permissions. These filters will act as "or" conditions.
      </Typography>
      <Grid container spacing={2} size={{ xs: 8 }}>
        <Grid size={{ xs: 12 }}>
          <Controller
            name="hasFileTypeFilter"
            control={control}
            render={({ field: props }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    inputProps={{
                      id: 'hasFileTypeFilter',
                    }}
                    {...props}
                    checked={props.value}
                    onChange={(e) => {
                      props.onChange(e.target.checked);
                      setValue('triggerInfo.fileMimeTypes', []);
                    }}
                  />
                }
                label="Include file type filter"
              />
            )}
          />

          <Box width={'100%'}>
            <Controller
              name="triggerInfo.fileMimeTypes"
              control={control}
              rules={{
                required: hasFileTypeFilter
                  ? 'Please select a file type.'
                  : false,
              }}
              render={({ field }) => (
                <Autocomplete
                  multiple
                  autoComplete
                  autoHighlight
                  options={documentTypeOptions}
                  getOptionLabel={(option) => option.displayValue}
                  disabled={!hasFileTypeFilter}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="File Types"
                      error={!!errors.triggerInfo?.fileMimeTypes}
                      helperText={
                        errors?.triggerInfo?.fileMimeTypes?.message || ' '
                      }
                    />
                  )}
                  ChipProps={{
                    size: 'small',
                  }}
                  filterSelectedOptions
                  fullWidth
                  value={field.value.map(
                    (value) =>
                      documentTypeOptions.find(
                        (option) => option.value === value
                      )!
                  )}
                  onChange={(_, data) => {
                    field.onChange(data.map((x) => x.value));
                    clearErrors('triggerInfo.fileMimeTypes');
                  }}
                  renderTags={(value, getTagProps) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.2 }}>
                      {value.map((option, index) => {
                        const file = documentTypeOptions.find(
                          (type) => type.value === option.value
                        )!;

                        return (
                          <Chip
                            {...getTagProps({ index })}
                            key={file.value}
                            label={file.displayValue}
                            size="small"
                            avatar={<Avatar src={file.icon} />}
                            sx={{
                              backgroundColor: '-moz-initial',
                            }}
                          />
                        );
                      })}
                    </Box>
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.value}>
                      <Avatar
                        src={option.icon}
                        sx={{ width: 20, height: 20, mr: 1 }}
                        variant="rounded"
                      />
                      {option.displayValue}
                    </li>
                  )}
                />
              )}
            />
          </Box>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Controller
            name="hasFileSizeFilter"
            control={control}
            render={({ field: props }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    inputProps={{
                      id: 'hasFileSizeFilter',
                    }}
                    {...props}
                    checked={props.value}
                    onChange={(e) => {
                      props.onChange(e.target.checked);
                      if (e.target.checked) {
                        setValue('sizeComparison', 'larger');
                        setValue('triggerInfo.driveFileSizeFloor', 1);
                        setValue('sizeType', 'MB');
                      } else {
                        setValue('sizeComparison', 'larger');
                        setValue('triggerInfo.driveFileSizeCeiling', 0);
                        setValue('triggerInfo.driveFileSizeFloor', 0);
                        setValue('sizeType', undefined);
                      }
                    }}
                  />
                }
                label="Include file size filter"
              />
            )}
          />
          <Stack flexDirection="row" gap={1} alignItems="center">
            <Controller
              name={'sizeComparison'}
              control={control}
              render={({ field }) => (
                <TextField
                  select
                  inputProps={{
                    id: 'sizeComparison',
                  }}
                  InputLabelProps={{
                    htmlFor: 'sizeComparison',
                  }}
                  {...field}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                  }}
                  label="Size Comparison"
                  sx={{ width: 150 }}
                  helperText={' '}
                  disabled={!hasFileSizeFilter}
                >
                  {sizeComparisonOptions.map((option) => (
                    <MenuItem value={option.value} key={option.value}>
                      {option.displayValue}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />

            {sizeComparison === 'smaller' ? (
              <Controller
                name={'triggerInfo.driveFileSizeCeiling'}
                control={control}
                render={({ field }) => (
                  <TextField
                    inputProps={{
                      id: 'ceiling',
                      min: 1,
                    }}
                    InputLabelProps={{
                      htmlFor: 'ceiling',
                    }}
                    type="number"
                    label="Size"
                    value={field.value}
                    onChange={(event) => {
                      if (event.target.value === '') {
                        field.onChange(event.target.value);
                      } else if (event.target.value === 'e') {
                        field.onChange('');
                      } else {
                        field.onChange(parseInt(event.target.value));
                      }
                    }}
                    sx={{ width: 70 }}
                    autoComplete="off"
                    error={!!errors.triggerInfo?.driveFileSizeCeiling}
                    helperText={
                      errors?.triggerInfo?.driveFileSizeCeiling?.message || ' '
                    }
                    FormHelperTextProps={{
                      sx: { textWrap: 'nowrap' },
                    }}
                    disabled={!hasFileSizeFilter}
                  />
                )}
              />
            ) : sizeComparison === 'larger' ? (
              <Controller
                name={'triggerInfo.driveFileSizeFloor'}
                control={control}
                render={({ field }) => (
                  <TextField
                    inputProps={{
                      id: 'floor',
                      min: 1,
                    }}
                    InputLabelProps={{
                      htmlFor: 'floor',
                    }}
                    type="number"
                    label="Size"
                    value={field.value}
                    onChange={(event) => {
                      if (event.target.value === '') {
                        field.onChange(event.target.value);
                      } else if (event.target.value === 'e') {
                        field.onChange('');
                      } else {
                        field.onChange(parseInt(event.target.value));
                      }
                    }}
                    sx={{ width: 70 }}
                    autoComplete="off"
                    error={!!errors.triggerInfo?.driveFileSizeFloor}
                    helperText={
                      errors?.triggerInfo?.driveFileSizeFloor?.message || ' '
                    }
                    FormHelperTextProps={{
                      sx: { textWrap: 'nowrap' },
                    }}
                    disabled={!hasFileSizeFilter}
                  />
                )}
              />
            ) : (
              <>
                <Controller
                  name={'triggerInfo.driveFileSizeFloor'}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      inputProps={{
                        id: 'floor',
                        min: 1,
                      }}
                      InputLabelProps={{
                        htmlFor: 'floor',
                      }}
                      type="number"
                      label="Size"
                      value={field.value}
                      onChange={(event) => {
                        if (event.target.value === '') {
                          field.onChange(event.target.value);
                        } else if (event.target.value === 'e') {
                          field.onChange('');
                        } else {
                          field.onChange(parseInt(event.target.value));
                        }
                      }}
                      sx={{ width: 70 }}
                      autoComplete="off"
                      helperText={' '}
                      disabled={!hasFileSizeFilter}
                    />
                  )}
                />
                <Typography
                  sx={{
                    mb: 2.5,
                  }}
                >
                  and
                </Typography>
                <Controller
                  name={'triggerInfo.driveFileSizeCeiling'}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      inputProps={{
                        id: 'ceiling',
                        min: 1,
                      }}
                      InputLabelProps={{
                        htmlFor: 'ceiling',
                      }}
                      type="number"
                      label="Size"
                      value={field.value}
                      onChange={(event) => {
                        if (event.target.value === '') {
                          field.onChange(event.target.value);
                        } else if (event.target.value === 'e') {
                          field.onChange('');
                        } else {
                          field.onChange(parseInt(event.target.value));
                        }
                      }}
                      sx={{ width: 70 }}
                      autoComplete="off"
                      error={!!errors.triggerInfo?.driveFileSizeCeiling}
                      helperText={
                        errors?.triggerInfo?.driveFileSizeCeiling?.message ||
                        ' '
                      }
                      FormHelperTextProps={{
                        sx: { textWrap: 'nowrap' },
                      }}
                      disabled={!hasFileSizeFilter}
                    />
                  )}
                />
              </>
            )}
            <Controller
              name={'sizeType'}
              control={control}
              render={({ field }) => (
                <TextField
                  select
                  inputProps={{
                    id: 'sizeType',
                  }}
                  InputLabelProps={{
                    htmlFor: 'sizeType',
                  }}
                  {...field}
                  label="File Size"
                  sx={{ width: 80 }}
                  helperText={' '}
                  disabled={!hasFileSizeFilter}
                >
                  {sizeTypeOptions.map((option) => (
                    <MenuItem value={option.value} key={option.value}>
                      {option.displayValue}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Stack>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Stack flexDirection="column">
            <Controller
              name="hasSharingPermissionsFilter"
              control={control}
              render={({ field: props }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      inputProps={{
                        id: 'hasSharingPermissionsFilter',
                      }}
                      {...props}
                      checked={props.value}
                      onChange={(e) => {
                        props.onChange(e.target.checked);
                        setValue(
                          'selectedSharingOption',
                          'fileSharedToUserExternalToDomain'
                        );
                        setValue('hasSelectedDomainSharing', false);
                        setValue('triggerInfo.fileSharedToSpecificUser', []);
                        setValue('triggerInfo.canFindInSearchResults', false);
                        setValue('triggerInfo.mustHaveLinkToAccess', false);
                      }}
                    />
                  }
                  label="Include file sharing filter"
                  sx={{ width: 'fit-content' }}
                />
              )}
            />
            <FormControl sx={{ ml: 4 }}>
              <FormLabel id="general-access-label">Shared to...</FormLabel>
              <RadioGroup
                aria-labelledby="general-access-label"
                name="general-access"
                value={selectedSharingOption}
                onChange={(event) => {
                  setValue(
                    'selectedSharingOption',
                    event.target.value as
                      | 'fileIsSharedToDomain'
                      | 'fileIsSharedPublic'
                      | 'fileSharedToSpecificUser'
                      | 'fileSharedToUserExternalToDomain'
                      | 'fileSharedToUserInternalToDomain'
                  );
                  setValue('hasSelectedDomainSharing', false);
                  setValue('triggerInfo.fileSharedToSpecificUser', []);
                  setValue('triggerInfo.canFindInSearchResults', false);
                  setValue('triggerInfo.mustHaveLinkToAccess', false);
                }}
                sx={{ ml: 1, width: 'fit-content' }}
              >
                {userSharingRadioOptions.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    control={<Radio size="small" />}
                    label={option.displayValue}
                    disabled={!hasSharingPermissionsFilter}
                  />
                ))}
              </RadioGroup>
            </FormControl>
            {hasSharingPermissionsFilter &&
              selectedSharingOption === 'fileIsSharedToDomain' && (
                <Controller
                  name="hasSelectedDomainSharing"
                  control={control}
                  render={({ field: props }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          inputProps={{
                            id: 'hasSelectedDomainSharing',
                          }}
                          {...props}
                          checked={props.value}
                          onChange={(e) => {
                            props.onChange(e.target.checked);
                            setValue(
                              'triggerInfo.canFindInSearchResults',
                              e.target.checked
                            );
                            setValue('triggerInfo.mustHaveLinkToAccess', false);
                          }}
                        />
                      }
                      label="Include more specific domain filter"
                      sx={{ width: 'fit-content' }}
                    />
                  )}
                />
              )}
            {hasSharingPermissionsFilter &&
              hasSelectedDomainSharing &&
              selectedSharingOption === 'fileIsSharedToDomain' && (
                <FormControl sx={{ ml: 4 }}>
                  <RadioGroup
                    aria-labelledby="domain-access-label"
                    name="domain-access"
                    value={
                      canFindInSearchResults
                        ? 'canFindInSearchResults'
                        : 'mustHaveLinkToAccess'
                    }
                    onChange={(event) => {
                      if (event.target.value === 'canFindInSearchResults') {
                        setValue('triggerInfo.canFindInSearchResults', true);
                        setValue('triggerInfo.mustHaveLinkToAccess', false);
                      } else {
                        setValue('triggerInfo.canFindInSearchResults', false);
                        setValue('triggerInfo.mustHaveLinkToAccess', true);
                      }
                    }}
                    sx={{ ml: 1, width: 'fit-content' }}
                  >
                    {domainSharingRadioOptions.map((option) => (
                      <FormControlLabel
                        key={option.value}
                        value={option.value}
                        control={<Radio size="small" />}
                        label={option.displayValue}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              )}
            {hasSharingPermissionsFilter &&
              selectedSharingOption === 'fileSharedToSpecificUser' && (
                <Stack flexDirection="row" gap={1}>
                  <Controller
                    name="triggerInfo.fileSharedToSpecificUser"
                    control={control}
                    rules={{
                      required: 'Please select a user.',
                    }}
                    render={() => (
                      <Box sx={{ width: { xs: '100%', lg: '50%' } }}>
                        <UsersAutocomplete
                          newKey={`${autocompleteKey.current}user`}
                          autocompleteValue={autoCompleteResponse}
                          setAutoCompleteValue={(value) => {
                            setAutoCompleteResponse(value);
                            if (!fileSharedToSpecificUser.includes(value))
                              setValue('triggerInfo.fileSharedToSpecificUser', [
                                ...fileSharedToSpecificUser,
                                value,
                              ]);
                            autocompleteKey.current = new Date()
                              .getTime()
                              .toString();
                            setAutoCompleteResponse('');
                            trigger('triggerInfo.fileSharedToSpecificUser');
                          }}
                          errors={
                            !!errors.triggerInfo?.fileSharedToSpecificUser
                          }
                          helperText={
                            errors?.triggerInfo?.fileSharedToSpecificUser
                              ?.message || undefined
                          }
                        />
                      </Box>
                    )}
                  />
                </Stack>
              )}
          </Stack>
        </Grid>
      </Grid>
      <Stack flexDirection="row" gap={1} sx={{ flexWrap: 'wrap' }}>
        {fileSharedToSpecificUser.length > 0 &&
          fileSharedToSpecificUser.map((source) => (
            <AvatarChip
              key={source}
              email={source}
              type={'User'}
              onDelete={() => {
                setValue(
                  'triggerInfo.fileSharedToSpecificUser',
                  fileSharedToSpecificUser.filter((x) => x !== source)
                );
              }}
              size="small"
              label={source}
            />
          ))}
      </Stack>
    </Stack>
  );
}
